import { useEffect, useState } from "react";

import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useDebounce } from "@uidotdev/usehooks";
import { DateTime } from "luxon";
import { useNavigate, useSearchParams } from "react-router-dom";

import ButtonGroup from "../../components/shared/ButtonGroup";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { usePartProTradeBidsPage } from "../../data/usePartProTradeBids";
import { IPartProTradeBidVehicle, PartProBidsTab } from "../../models/partPro/IPartProTradeBids";
import formatDateTime from "../../utils/formatDateTime";
import { describeVehicleRejection } from "../../utils/partProTradeReject";
import PriceFormat from "../../utils/priceFormat";

const PAGE_SIZE = 25;

const TABS: { id: PartProBidsTab; label: string; empty: string }[] = [
  { id: "open", label: "Open", empty: "No vehicles are open for bidding." },
  { id: "closed", label: "Closed", empty: "No vehicles are waiting for you to accept bids." },
  { id: "completed", label: "Purchased", empty: "No vehicles have been purchased yet." },
  { id: "rejected", label: "Rejected", empty: "No vehicles have been rejected." },
];

const parseTab = (raw: string | null): PartProBidsTab =>
  TABS.some((t) => t.id === raw) ? (raw as PartProBidsTab) : "open";

function Outcome({ vehicle, tab }: { vehicle: IPartProTradeBidVehicle; tab: PartProBidsTab }) {
  if (tab === "open") {
    if (!vehicle.biddingEndsAt) return <span className="text-gray-500">-</span>;
    return (
      <div>
        <p className="whitespace-nowrap">{formatDateTime(vehicle.biddingEndsAt, DateTime.DATETIME_SHORT)}</p>
        <p className="text-xs text-gray-500">{DateTime.fromISO(vehicle.biddingEndsAt).toRelative()}</p>
      </div>
    );
  }
  if (tab === "closed") {
    return (
      <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
        {vehicle.pendingLotCount} to decide
      </span>
    );
  }
  if (tab === "rejected") {
    return (
      <div>
        <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700">
          {describeVehicleRejection(vehicle)}
        </span>
        {vehicle.rejectedAt && (
          <p className="mt-1 text-xs text-gray-500">{formatDateTime(vehicle.rejectedAt, DateTime.DATE_MED)}</p>
        )}
      </div>
    );
  }
  return (
    <span className="text-gray-700">
      {vehicle.awardedLotCount} of {vehicle.lotCount} sold
    </span>
  );
}

export default function PartProBids() {
  const navigate = useNavigate();
  const { setInfo } = usePageNameContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseTab(searchParams.get("tab"));
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(searchParams.get("q") ?? "");
  const query = useDebounce(search.trim(), 400);

  // A new search starts from the first page
  useEffect(() => setPage(1), [query]);

  const bids = usePartProTradeBidsPage(tab, query, page, PAGE_SIZE);
  const data = bids.data?.data;

  useEffect(() => {
    setInfo({
      name: "Bids",
      desc: "PartPro Trade bids on your vehicles - accept the winning offers once bidding closes.",
    });
  }, []);

  const changeTab = (next: string) => {
    setPage(1);
    setSearchParams(query ? { tab: next, q: query } : { tab: next });
  };

  const countFor = (id: PartProBidsTab) =>
    data
      ? { open: data.openCount, closed: data.closedCount, completed: data.completedCount, rejected: data.rejectedCount }[id]
      : null;

  return (
    <div className="mt-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <ButtonGroup
          size="sm"
          options={TABS.map((t) => {
            const count = countFor(t.id);
            return { id: t.id, label: count == null ? t.label : `${t.label} (${count})`, value: t.id };
          })}
          selectedIndex={TABS.findIndex((t) => t.id === tab)}
          onChange={changeTab}
        />
        <div className="relative w-full sm:w-80">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search registration, VIN, make or model"
            className="block w-full rounded-md border-gray-300 pl-9 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
          />
        </div>
      </div>

      {bids.isLoading ? (
        <LoadingWheel />
      ) : bids.isError || !data ? (
        <div className="rounded-lg bg-white p-6 shadow">
          <h2 className="text-lg font-semibold text-gray-900">PartPro Trade bids unavailable</h2>
          <p className="mt-1 text-sm text-gray-500">
            {bids.error instanceof Error ? bids.error.message : "PartPro did not return your vehicles."}
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg bg-white shadow">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Vehicle</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Categories</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Bids</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">
                  {tab === "completed" ? "Sold for" : "Highest bids total"}
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">
                  {tab === "open" ? "Bidding ends" : tab === "closed" ? "Decisions" : "Outcome"}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.vehicles.map((v) => (
                <tr
                  key={v.id}
                  onClick={() => navigate(`/bids/${v.id}`)}
                  className="cursor-pointer hover:bg-gray-50"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {v.photo ? (
                        <img src={v.photo} alt="" className="h-10 w-14 flex-none rounded object-cover" />
                      ) : (
                        <div className="h-10 w-14 flex-none rounded bg-gray-100" />
                      )}
                      <div>
                        <p className="font-medium text-gray-900">{v.name}</p>
                        {v.vrm && <p className="text-xs text-gray-500">{v.vrm}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {v.lotsWithBids} of {v.lotCount} bid on
                  </td>
                  <td className="px-4 py-3 text-gray-700">{v.activeBidCount}</td>
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {tab === "completed"
                      ? v.awardedLotCount > 0
                        ? PriceFormat(v.awardedTotal)
                        : "-"
                      : v.activeBidCount > 0
                        ? PriceFormat(v.highestBidTotal)
                        : "-"}
                  </td>
                  <td className="px-4 py-3">
                    <Outcome vehicle={v} tab={tab} />
                  </td>
                </tr>
              ))}
              {data.vehicles.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-gray-500">
                    {query
                      ? `No ${TABS.find((t) => t.id === tab)!.label.toLowerCase()} vehicles match "${query}".`
                      : TABS.find((t) => t.id === tab)!.empty}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {data.totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 text-sm">
              <span className="text-gray-600">
                Page {data.page} of {data.totalPages} · {data.total} vehicles
              </span>
              <div className="flex gap-2">
                <ButtonInput
                  label="Previous"
                  isSubmit={false}
                  classes=""
                  size="sm"
                  disabled={data.page <= 1 || bids.isFetching}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                />
                <ButtonInput
                  label="Next"
                  isSubmit={false}
                  classes=""
                  size="sm"
                  disabled={data.page >= data.totalPages || bids.isFetching}
                  onClick={() => setPage((p) => p + 1)}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
